import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { ctw } from '@/common/utils/ctw';

const badgeVariants = cva(
  'flex inline-flex cursor-default items-center justify-center gap-1 rounded-full px-3 py-1 transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        outline: 'text-foreground',
        primary: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        info: 'bg-info/20 text-info',
        success: 'bg-success/20 text-success',
        warning: 'bg-warning/20 text-warning',
        destructive: 'bg-destructive/20 text-destructive',
        violet: 'bg-violet/20 text-violet',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

export type BadgeVariantProps = VariantProps<typeof badgeVariants>;

export type BadgeProps = {
  asChild?: boolean;
} & React.HTMLAttributes<HTMLDivElement> &
  BadgeVariantProps;

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, children, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';

    return (
      <Comp className={ctw(badgeVariants({ variant }), className)} {...props} ref={ref}>
        {children}
      </Comp>
    );
  },
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
